import React, { useState, useEffect } from 'react';

// At the top of your component or in a separate utilities file
import axiosInstance from '../../src/axiosConfig'; // Import the configured Axios instance
import GlassPaper from '../components/GlassPaper';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import './HomePage.css';

const HomePage = () => {
    const [backendMessage, setBackendMessage] = useState('');

    useEffect(() => {
        const fetchMessage = async () => {
            try {
                const response = await axiosInstance.get('/api/hello');
                setBackendMessage(response.data.message);  // Assuming the backend sends a response in { message: "Hello World" }
            } catch (error) {
                setBackendMessage('Failed to fetch data from backend');
            }
        };

        fetchMessage();
    }, []);

    const isSystemHealthy = backendMessage === 'Hello World!';
    const healthStatusLabel = isSystemHealthy ? 'Operational' : 'Needs Attention';
    const healthStatusMessage = isSystemHealthy
        ? `All APIs are responding normally. ${backendMessage || 'Everything looks good!'}`.trim()
        : 'We are experiencing disruptions. Please reach out to your IT admin for support.';
    const healthPulseTone = isSystemHealthy ? 'success' : 'warning';
    const complianceCards = [
        {
            key: 'legal',
            eyebrow: 'Legal Notice',
            title: 'Restricted access',
            badge: 'Policy',
            description:
                'Contra - Insights is a protected environment. Access is limited to authorized employees and contractors operating within defined scopes of work.',
            bullets: [
                'Usage grants are issued individually and must never be shared.',
                'Attempted access without approval is logged and escalated.',
            ],
            footer: 'Continuing affirms acknowledgement of corporate policy INF-01.',
            icon: GavelRoundedIcon,
            cta: { label: 'Policy handbook (coming soon)', disabled: true },
        },
        {
            key: 'important',
            eyebrow: 'Important Notice',
            title: 'Authorized personnel only',
            badge: 'Awareness',
            description:
                'All activity within Insights is monitored in real time. Interactions should only support business objectives aligned to your role.',
            bullets: [
                'Data classifications must be respected at all times.',
                'Sensitive exports require prior approval from Security Operations.',
            ],
            footer: 'Questions? Reach SecurityOps via Slack #ask-securityops.',
            icon: PriorityHighRoundedIcon,
            cta: { label: 'View acceptable use', href: '#', disabled: true },
        },
        {
            key: 'restrictions',
            eyebrow: 'Access Restrictions',
            title: 'Continuous enforcement',
            badge: 'Security',
            description:
                'Identity, device posture, and location are validated on every sign-in. Sessions auto-expire when anomalies are detected.',
            bullets: [
                'SSO + MFA enforced for every login.',
                'Accounts deactivate after 30 days of inactivity.',
            ],
            footer: 'The platform automatically revokes stale sessions.',
            icon: SecurityRoundedIcon,
            cta: { label: 'Review access tiers', href: '#', disabled: true },
        },
        {
            key: 'consent',
            eyebrow: 'Consent to Monitoring',
            title: 'Telemetry in effect',
            badge: 'Compliance',
            description:
                'Usage analytics help protect customers and the enterprise. Continuing to work in Insights signifies consent to these controls.',
            bullets: [
                'Log retention follows SOC 2 and ISO 27001 schedules.',
                'Evidence is preserved for potential legal proceedings.',
            ],
            footer: 'Do not proceed if you decline monitoring.',
            icon: VisibilityRoundedIcon,
            cta: { label: 'Monitoring charter', href: '#', disabled: true },
        },
    ];

    return (
        <div className="container">
            <header className="py-5">
                <h1>Contra - Insights</h1>
                <br />
                <div className="row g-4 home-card-row home-card-row--operations">
                    <div className="col-12 col-lg-4 d-flex">
                        <GlassPaper
                            component="section"
                            className={`home-card home-card--balanced home-card--health home-card--${healthPulseTone} w-100`}
                        >
                            <div className="home-card__top">
                                <div className="home-card__icon">
                                    <HealthAndSafetyRoundedIcon fontSize="inherit" />
                                </div>
                                <div>
                                    <span className="home-card__eyebrow">System Health</span>
                                    <h3 className="home-card__title">API environment</h3>
                                </div>
                                <span className="home-card__badge">{healthStatusLabel}</span>
                            </div>
                            <p className="home-card__description">{healthStatusMessage}</p>
                            <div className="home-card__status">
                                <span className="home-card__pulse" aria-hidden="true" />
                                <span>{isSystemHealthy ? 'No action needed at this time.' : 'Incident response engaged.'}</span>
                            </div>
                            <div className="home-card__spacer" />
                            <a href="/" className="home-card__cta" aria-label="Report an issue with Insights">
                                Report Issue
                                <ArrowOutwardRoundedIcon fontSize="small" />
                            </a>
                        </GlassPaper>
                    </div>
                    <div className="col-12 col-lg-4 d-flex">
                        <GlassPaper
                            component="section"
                            className="home-card home-card--maintenance home-card--balanced w-100"
                        >
                            <div className="home-card__top">
                                <div className="home-card__icon">
                                    <BuildCircleRoundedIcon fontSize="inherit" />
                                </div>
                                <div>
                                    <span className="home-card__eyebrow">System Maintenance</span>
                                    <h3 className="home-card__title">Upcoming window</h3>
                                </div>
                                <span className="home-card__badge">Scheduled</span>
                            </div>
                            <p className="home-card__description">
                                Routine maintenance is planned to keep Insights performant and secure. Expect short service pauses while we deploy the next release.
                            </p>
                            <ul className="home-card__list">
                                <li>
                                    <span className="home-card__bullet" aria-hidden="true" />
                                    Maintenance window announced 72 hours in advance.
                                </li>
                                <li>
                                    <span className="home-card__bullet" aria-hidden="true" />
                                    Change log shared with impacted teams.
                                </li>
                                <li>
                                    <span className="home-card__bullet" aria-hidden="true" />
                                    Post-release validation completes within 30 minutes.
                                </li>
                            </ul>
                            <div className="home-card__spacer" />
                            <span className="home-card__cta home-card__cta--disabled" aria-label="Maintenance notifications coming soon">
                                Notification opt-in coming soon
                            </span>
                        </GlassPaper>
                    </div>
                    <div className="col-12 col-lg-4 d-flex">
                        <GlassPaper
                            component="section"
                            className="home-card home-card--access home-card--balanced w-100"
                        >
                            <div className="home-card__top">
                                <div className="home-card__icon">
                                    <LockOpenRoundedIcon fontSize="inherit" />
                                </div>
                                <div>
                                    <span className="home-card__eyebrow">Access System</span>
                                    <h3 className="home-card__title">Insights workspace</h3>
                                </div>
                                <span className="home-card__badge">Secure</span>
                            </div>
                            <p className="home-card__description">
                                Launch the Insights application to explore dashboards, monitor access, and manage your organization&apos;s Contra footprint securely.
                            </p>
                            <div className="home-card__status home-card__status--secondary">
                                <span className="home-card__pulse" aria-hidden="true" />
                                <span>SSO enforced · Activity monitored</span>
                            </div>
                            <div className="home-card__spacer" />
                            <a href="/access" className="home-card__cta" aria-label="Open the Insights application">
                                Enter Insights
                                <ArrowOutwardRoundedIcon fontSize="small" />
                            </a>
                        </GlassPaper>
                    </div>
                </div>
            </header>
            <section className="home-section">
                <div className="home-section__header">
                    <div>
                        <span className="home-section__eyebrow">Governance & Compliance</span>
                        <h2 className="home-section__title">Usage guidelines and accountability</h2>
                        <p className="home-section__subtitle">
                            Review the operating guardrails that protect Contra - Insights for every session.
                        </p>
                    </div>
                </div>
                <div className="row g-4 home-card-row home-card-row--compliance">
                    {complianceCards.map((card) => {
                        const CardIcon = card.icon;

                        return (
                            <div className="col-12 col-lg-6 d-flex" key={card.key}>
                                <GlassPaper
                                    component="section"
                                    className={`home-card home-card--balanced home-card--info home-card--${card.key} w-100`}
                                >
                                    <div className="home-card__top">
                                        <div className="home-card__icon">
                                            <CardIcon fontSize="inherit" />
                                        </div>
                                        <div>
                                            <span className="home-card__eyebrow">{card.eyebrow}</span>
                                            <h3 className="home-card__title">{card.title}</h3>
                                        </div>
                                        <span className="home-card__badge">{card.badge}</span>
                                    </div>
                                    <p className="home-card__description">{card.description}</p>
                                    {card.bullets && (
                                        <ul className="home-card__list">
                                            {card.bullets.map((item, index) => (
                                                <li key={index}>
                                                    <span className="home-card__bullet" aria-hidden="true" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {card.footer && (
                                        <div className="home-card__status home-card__status--secondary">
                                            <span className="home-card__pulse" aria-hidden="true" />
                                            <span>{card.footer}</span>
                                        </div>
                                    )}
                                    <div className="home-card__spacer" />
                                    {card.cta ? (
                                        card.cta.disabled ? (
                                            <span className="home-card__cta home-card__cta--disabled">
                                                {card.cta.label}
                                            </span>
                                        ) : (
                                            <a href={card.cta.href} className="home-card__cta">
                                                {card.cta.label}
                                                <ArrowOutwardRoundedIcon fontSize="small" />
                                            </a>
                                        )
                                    ) : null}
                                </GlassPaper>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default HomePage;
